import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPackingSlipData } from "@/lib/operations.functions";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/orders/$orderId/packing-slip")({
  component: PackingSlipPage,
});

function PackingSlipPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();

  const fetchPackingSlip = useServerFn(getPackingSlipData);
  const { data } = useQuery({
    queryKey: ["packing-slip", orderId],
    queryFn: () => fetchPackingSlip({ data: { orderId } }),
  });
  const order = data?.order;
  const lines = data?.lines ?? [];
  const skus = data?.skus ?? [];
  const customer = data?.customer;
  const tenant = data?.tenant;
  const shipment = data?.shipment;

  useEffect(() => { document.title = order ? `Packing slip ${order.order_number}` : "Packing slip"; }, [order]);

  const skuMap = Object.fromEntries(skus.map((s) => [s.id, s]));
  const ship = (order?.ship_to ?? {}) as { name?: string; address?: string };

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="print:hidden border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate({ to: "/app/orders/$orderId", params: { orderId } })}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back to order
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="bg-white text-black p-10 shadow-sm print:shadow-none print:p-0">
          <header className="flex items-start justify-between border-b border-black/20 pb-5">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Packing Slip</h1>
              <div className="text-sm text-black/60 mt-1">{tenant?.name ?? ""}</div>
            </div>
            <div className="text-right text-sm">
              <div className="text-black/60">Order #</div>
              <div className="text-xl font-semibold">{order?.order_number ?? "—"}</div>
              <div className="text-black/60 mt-2">Date</div>
              <div>{order ? new Date(order.created_at).toLocaleDateString() : ""}</div>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-8 my-6">
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50 mb-1">Sold by</div>
              <div className="font-medium">{customer?.name ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50 mb-1">Ship to</div>
              <div className="font-medium">{ship.name ?? "—"}</div>
              <div className="text-sm whitespace-pre-line">{ship.address ?? ""}</div>
            </div>
          </section>

          <section className="grid grid-cols-3 gap-4 mb-6 text-sm border-y border-black/10 py-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50">Carrier</div>
              <div>{shipment?.carrier ?? order?.carrier ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50">Service</div>
              <div>{shipment?.service_level ?? order?.service_level ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50">Tracking</div>
              <div className="font-mono">{shipment?.tracking_number ?? "—"}</div>
            </div>
          </section>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/30 text-left">
                <th className="py-2">SKU</th>
                <th>Description</th>
                <th className="text-right">Ordered</th>
                <th className="text-right">Shipped</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const s = skuMap[l.sku_id];
                return (
                  <tr key={l.id} className="border-b border-black/10">
                    <td className="py-2 font-mono">{s?.sku_code ?? "—"}</td>
                    <td>{s?.name ?? ""}</td>
                    <td className="text-right tabular-nums">{l.qty_ordered}</td>
                    <td className="text-right tabular-nums">{l.qty_shipped ?? 0}</td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-black/50">No lines.</td></tr>
              )}
            </tbody>
          </table>

          <footer className="mt-10 pt-4 border-t border-black/10 text-xs text-black/50 flex justify-between">
            <span>Thank you for your order.</span>
            <span>Generated {new Date().toLocaleString()}</span>
          </footer>
        </div>
      </div>
    </div>
  );
}